import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Inbox, Send, PenSquare, BarChart3, RefreshCw, Power, Mail,
  Archive, Eye, EyeOff, Workflow, Lock, Activity,
  Check, CheckCheck, MailOpen, MousePointerClick, AlertTriangle, ShieldAlert,
  Clock, Ban, Calendar, Filter, X, ArrowUpDown, Reply, FileText, LayoutTemplate,
} from "lucide-react";
import DevicePreviewIframe from "../components/email/DevicePreviewIframe";
import ComposerForm from "./emails/ComposerForm";
import TemplatesTab from "./emails/TemplatesTab";
import { useEmailFilters } from "./emails/useEmailFilters";
import { usePolling } from "./emails/usePolling";

const TABS = [
  { id: "outbound", label: "Enviados", Icon: Send },
  { id: "inbound", label: "Recibidos", Icon: Inbox },
  { id: "events", label: "Eventos", Icon: Activity },
  { id: "sequences", label: "Secuencias", Icon: Workflow },
  { id: "templates", label: "Templates", Icon: LayoutTemplate },
  { id: "compose", label: "Componer", Icon: PenSquare },
  { id: "stats", label: "Estadísticas", Icon: BarChart3 },
];

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "bg-blue-100", fg: "text-blue-800", label: "Enviado" },
    scheduled: { bg: "bg-slate-100", fg: "text-slate-800", label: "Programado" },
    delivered: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Entregado" },
    delayed: { bg: "bg-amber-100", fg: "text-amber-800", label: "Demorado" },
    opened: { bg: "bg-violet-100", fg: "text-violet-800", label: "Abierto" },
    clicked: { bg: "bg-fuchsia-100", fg: "text-fuchsia-800", label: "Click" },
    bounced: { bg: "bg-red-100", fg: "text-red-800", label: "Bounced" },
    complained: { bg: "bg-orange-100", fg: "text-orange-800", label: "Spam" },
    failed: { bg: "bg-red-100", fg: "text-red-700", label: "Fallido" },
    suppressed: { bg: "bg-gray-200", fg: "text-gray-800", label: "Suprimido" },
    killed: { bg: "bg-gray-200", fg: "text-gray-700", label: "Bloqueado (off)" },
    sequence_disabled: { bg: "bg-amber-100", fg: "text-amber-800", label: "Secuencia inactiva" },
    error: { bg: "bg-red-100", fg: "text-red-700", label: "Error" },
    skipped: { bg: "bg-gray-100", fg: "text-gray-600", label: "Omitido" },
    pending: { bg: "bg-amber-100", fg: "text-amber-800", label: "Pendiente" },
  };
  const s = map[status] || { bg: "bg-gray-100", fg: "text-gray-700", label: status || "—" };
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-sm ${s.bg} ${s.fg}`}>{s.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminEmailsPage() {
  const [activeTab, setActiveTab] = useState("outbound");
  const [stats, setStats] = useState(null);
  const [emailsEnabled, setEmailsEnabled] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/emails/stats");
      setStats(data);
      setEmailsEnabled(Boolean(data?.emails_enabled));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando estadísticas");
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const toggleEmails = async (next) => {
    setSavingToggle(true);
    try {
      await api.post("/admin/settings", { key: "emails_enabled", value: !!next });
      setEmailsEnabled(!!next);
      toast.success(next ? "Envío de emails ACTIVADO" : "Envío de emails DESACTIVADO (kill switch)");
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error actualizando flag");
    } finally {
      setSavingToggle(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-gray-900 mb-1 flex items-center gap-2">
            <Mail className="w-7 h-7 text-brand-burgundy" />
            Bandeja de correo
          </h1>
          <p className="text-sm text-gray-600">Emails enviados desde JustSapiens Procure y respuestas recibidas (Resend).</p>
        </div>
        <div
          className={`flex items-center gap-3 px-4 py-3 border rounded-sm ${
            emailsEnabled ? "border-emerald-300 bg-emerald-50" : "border-amber-400 bg-amber-50"
          }`}
          data-testid="email-kill-switch"
        >
          <Power className={`w-5 h-5 ${emailsEnabled ? "text-emerald-700" : "text-amber-700"}`} />
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
              Envío global de emails
            </div>
            <div className="text-[11px] text-gray-600 max-w-[260px]">
              {emailsEnabled
                ? "Activado. Resend procesa transaccionales y composer."
                : "DESACTIVADO. Nada se envía ni se recibe (incluye críticos)."}
            </div>
          </div>
          <Switch
            checked={emailsEnabled}
            onCheckedChange={toggleEmails}
            disabled={savingToggle}
            data-testid="email-toggle"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-6">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-brand-burgundy text-brand-burgundy"
                : "border-transparent text-gray-600 hover:text-brand-burgundy"
            }`}
            data-testid={`email-tab-${id}`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {id === "inbound" && stats?.inbound?.unread > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-brand-coral text-white">
                {stats.inbound.unread}
              </span>
            )}
          </button>
        ))}
      </div>

      <div>
        {activeTab === "outbound" && <OutboundTab onChange={loadStats} />}
        {activeTab === "inbound" && <InboundTab onChange={loadStats} />}
        {activeTab === "events" && <EventsTab />}
        {activeTab === "sequences" && <SequencesTab />}
        {activeTab === "templates" && <TemplatesTab />}
        {activeTab === "compose" && <ComposeTab emailsEnabled={emailsEnabled} onSent={loadStats} />}
        {activeTab === "stats" && <StatsTab stats={stats} onReload={loadStats} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Outbound tab
// ─────────────────────────────────────────────────────────────────────────

function OutboundTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const filters = useEmailFilters({
    q: "",
    status: "",
    template: "",
    email_search: "",
    email_field: "any",
    date_from: "",
    date_to: "",
    sort_by: "created_at",
    sort_dir: "desc",
  });

  // Cargar templates únicos (desde stats endpoint)
  useEffect(() => {
    api.get("/admin/emails/stats").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filters.toQueryParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/admin/emails/outbound?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando emails");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  // Polling cada 15s para que sent/delivered/opened aparezcan en tiempo real
  usePolling(load, 15000, [load]);

  const resend = async (logId) => {
    try {
      await api.post(`/admin/emails/outbound/${logId}/resend`);
      toast.success("Email reenviado");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error reenviando");
    }
  };

  const openDetail = async (logId) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/emails/outbound/${logId}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error abriendo email");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div>
      <FilterPanel activeCount={filters.activeCount} onReset={() => { filters.reset(); setPage(1); }} onReload={load} loading={loading} testIdPrefix="outbound">
        <Input
          placeholder="Búsqueda libre (asunto, destinatario, template)"
          value={filters.state.q}
          onChange={(e) => filters.set("q", e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
          data-testid="outbound-search"
        />
        <select
          value={filters.state.status}
          onChange={(e) => { filters.set("status", e.target.value); setPage(1); }}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="outbound-status-filter"
        >
          <option value="">Todos los estados</option>
          <option value="sent">Enviado</option>
          <option value="scheduled">Programado</option>
          <option value="delivered">Entregado</option>
          <option value="opened">Abierto</option>
          <option value="clicked">Click</option>
          <option value="bounced">Bounced</option>
          <option value="complained">Spam</option>
          <option value="failed">Fallido</option>
          <option value="suppressed">Suprimido</option>
          <option value="killed">Bloqueado (off)</option>
          <option value="sequence_disabled">Secuencia inactiva</option>
          <option value="error">Error</option>
          <option value="skipped">Omitido</option>
        </select>
        <select
          value={filters.state.template}
          onChange={(e) => { filters.set("template", e.target.value); setPage(1); }}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="outbound-template-filter"
        >
          <option value="">Todos los templates</option>
          {templates.map((t) => <option key={t} value={t}>{t}</option>)}
          <option value="admin_compose">admin_compose</option>
        </select>
        <EmailSearchInput
          email={filters.state.email_search}
          field={filters.state.email_field}
          onChange={(email, field) => { filters.setMany({ email_search: email, email_field: field }); setPage(1); }}
          testIdPrefix="outbound"
        />
        <Input
          type="date"
          value={filters.state.date_from}
          onChange={(e) => { filters.set("date_from", e.target.value); setPage(1); }}
          data-testid="outbound-date-from"
          className="max-w-[150px]"
          title="Desde"
        />
        <Input
          type="date"
          value={filters.state.date_to}
          onChange={(e) => { filters.set("date_to", e.target.value); setPage(1); }}
          data-testid="outbound-date-to"
          className="max-w-[150px]"
          title="Hasta"
        />
        <SortControl
          value={filters.state.sort_by}
          dir={filters.state.sort_dir}
          options={[
            { v: "created_at", l: "Fecha" },
            { v: "status", l: "Estado" },
            { v: "to_email", l: "Destinatario" },
            { v: "subject", l: "Asunto" },
            { v: "template", l: "Template" },
          ]}
          onChange={(v, d) => filters.setMany({ sort_by: v, sort_dir: d })}
          testId="outbound-sort"
        />
      </FilterPanel>

      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="text-left p-3 font-medium">Fecha</th>
              <th className="text-left p-3 font-medium">De</th>
              <th className="text-left p-3 font-medium">Para</th>
              <th className="text-left p-3 font-medium">Asunto</th>
              <th className="text-left p-3 font-medium">Template</th>
              <th className="text-left p-3 font-medium">Estado</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Sin emails que mostrar.</td></tr>
            )}
            {items.map((it) => {
              const toFull = it.to_emails && it.to_emails.length > 0 ? it.to_emails.join(", ") : it.to_email;
              return (
                <tr
                  key={it.id}
                  className="border-t border-gray-100 hover:bg-gray-50/70 cursor-pointer"
                  onClick={() => openDetail(it.id)}
                  data-testid={`outbound-row-${it.id}`}
                >
                  <td className="p-3 whitespace-nowrap text-gray-700">{fmtDate(it.created_at)}</td>
                  <td className="p-3 text-gray-800 max-w-[200px] truncate" title={it.from_email}>{it.from_email || <em className="text-gray-400">—</em>}</td>
                  <td className="p-3 text-gray-800 max-w-[200px] truncate" title={toFull}>{toFull}</td>
                  <td className="p-3 text-gray-800 max-w-[260px] truncate" title={it.subject}>{it.subject}</td>
                  <td className="p-3 text-xs text-gray-500">{it.template}</td>
                  <td className="p-3"><StatusBadge status={it.status} /></td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => resend(it.id)} data-testid={`resend-${it.id}`}>
                      Reenviar
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />

      {/* Modal detalle email enviado */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* a11y: DialogTitle siempre presente */}
          <DialogHeader className={selected ? "sr-only" : "sr-only"}>
            <DialogTitle>Detalle del email enviado</DialogTitle>
            <DialogDescription>{selected?.subject || "Cargando…"}</DialogDescription>
          </DialogHeader>
          {selected && (
            <OutboundDetail
              item={selected}
              onClose={() => setSelected(null)}
              onResend={() => { resend(selected.id); setSelected(null); }}
            />
          )}
          {loadingDetail && !selected && <div className="p-6 text-center text-gray-500">Cargando…</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente para mostrar detalle de un email enviado (modal)
function OutboundDetail({ item, onClose, onResend }) {
  const hasHtml = !!(item.html && item.html.trim());
  const wrappedHtml = hasHtml
    ? `<!doctype html><html><head><meta charset="utf-8"/><style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.55;color:#1f2937;padding:16px;margin:0;background:#fff;}
        a{color:#6F3947;text-decoration:underline;}
        img{max-width:100%;height:auto;}
        blockquote{border-left:3px solid #d4af37;padding-left:12px;color:#555;margin:8px 0;}
      </style></head><body>${item.html}</body></html>`
    : "";

  const toFull = item.to_emails && item.to_emails.length > 0 ? item.to_emails.join(", ") : item.to_email;
  const events = item.events_timeline || [];

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-base pr-8">{item.subject || "(sin asunto)"}</DialogTitle>
        <DialogDescription asChild>
          <div className="text-xs space-y-1 mt-2 text-muted-foreground">
            <div><strong className="text-gray-700">De:</strong> {item.from_email || <em>(default EMAIL_FROM)</em>}</div>
            <div><strong className="text-gray-700">Para:</strong> {toFull}</div>
            {item.cc_emails && item.cc_emails.length > 0 && (
              <div><strong className="text-gray-700">CC:</strong> {item.cc_emails.join(", ")}</div>
            )}
            {item.bcc_emails && item.bcc_emails.length > 0 && (
              <div><strong className="text-gray-700">BCC:</strong> {item.bcc_emails.join(", ")}</div>
            )}
            <div><strong className="text-gray-700">Enviado:</strong> {fmtDate(item.created_at)}</div>
            <div><strong className="text-gray-700">Template:</strong> <code className="bg-gray-100 px-1">{item.template}</code></div>
            <div><strong className="text-gray-700">Estado:</strong> <StatusBadge status={item.status} /></div>
            {item.provider_id && (
              <div><strong className="text-gray-700">Provider ID:</strong> <code className="bg-gray-100 px-1 text-[10px]">{item.provider_id}</code></div>
            )}
            {item.error && (
              <div className="text-red-700"><strong>Error:</strong> {item.error}</div>
            )}
          </div>
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4">
        {hasHtml ? (
          <DevicePreviewIframe srcDoc={wrappedHtml} testIdPrefix="outbound-preview" height={420} />
        ) : (
          <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-sm border border-gray-200 text-sm">
            <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            El cuerpo de este email no está disponible.
            {item.template && item.template !== "admin_compose" && (
              <div className="text-xs text-gray-400 mt-1">
                (Renderizado del template "{item.template}" no disponible)
              </div>
            )}
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs uppercase tracking-wide text-gray-600 font-semibold mb-2">
            Timeline de eventos ({events.length})
          </h4>
          <div className="border border-gray-200 rounded-sm bg-gray-50/50 p-2 max-h-[150px] overflow-y-auto">
            {events.map((e) => (
              <div key={e.id} className="flex items-center gap-2 py-1 text-xs">
                <span className="text-gray-400 font-mono w-32 shrink-0">{fmtDate(e.occurred_at)}</span>
                <StatusBadge status={e.status} />
                <span className="text-gray-500 ml-1">{e.event_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogFooter className="mt-4 flex-wrap gap-2">
        {onResend && (
          <Button onClick={onResend} variant="outline" data-testid="outbound-resend">
            Reenviar
          </Button>
        )}
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
      </DialogFooter>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Inbound tab
// ─────────────────────────────────────────────────────────────────────────

function InboundTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyComposer, setReplyComposer] = useState(null);
  const filters = useEmailFilters({
    q: "",
    unread_only: false,
    include_archived: false,
    email_search: "",
    email_field: "any",
    has_attachments: "",
    date_from: "",
    date_to: "",
    sort_by: "received_at",
    sort_dir: "desc",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filters.toQueryParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/admin/emails/inbound?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando inbound");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  // Polling cada 15s para emails recibidos en tiempo real desde Resend
  usePolling(load, 15000, [load]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/admin/emails/inbound/${id}`);
      setSelected(data);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error abriendo email");
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleRead = async (id, isRead) => {
    try {
      await api.patch(`/admin/emails/inbound/${id}`, { read: !isRead });
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error actualizando");
    }
  };

  const archive = async (id, isArchived) => {
    try {
      await api.patch(`/admin/emails/inbound/${id}`, { archived: !isArchived });
      toast.success(isArchived ? "Desarchivado" : "Archivado");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error archivando");
    }
  };

  const openReply = (item) => {
    // Quote del cuerpo: max 1500 chars del text plano
    const quote = (item.text || htmlToTextSimple(item.html || "") || "").slice(0, 1500);
    setReplyComposer({
      from_email: item.from_email,
      subject: item.subject,
      provider_id: item.provider_id,
      inbound_id: item.id,
      body_quote: quote.replace(/\n/g, "<br/>"),
    });
    setSelected(null);
  };

  return (
    <div>
      <FilterPanel activeCount={filters.activeCount} onReset={() => { filters.reset(); setPage(1); }} onReload={load} loading={loading} testIdPrefix="inbound">
        <Input
          placeholder="Búsqueda libre (remitente, asunto, cuerpo)"
          value={filters.state.q}
          onChange={(e) => filters.set("q", e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
          data-testid="inbound-search"
        />
        <EmailSearchInput
          email={filters.state.email_search}
          field={filters.state.email_field}
          onChange={(email, field) => { filters.setMany({ email_search: email, email_field: field }); setPage(1); }}
          testIdPrefix="inbound"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer h-10 px-3 border border-gray-300 rounded-sm bg-white">
          <input type="checkbox" checked={filters.state.unread_only} onChange={(e) => { filters.set("unread_only", e.target.checked); setPage(1); }} data-testid="inbound-unread" />
          Solo no leídos
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer h-10 px-3 border border-gray-300 rounded-sm bg-white">
          <input type="checkbox" checked={filters.state.include_archived} onChange={(e) => { filters.set("include_archived", e.target.checked); setPage(1); }} data-testid="inbound-archived" />
          Incluir archivados
        </label>
        <select
          value={filters.state.has_attachments}
          onChange={(e) => { filters.set("has_attachments", e.target.value); setPage(1); }}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="inbound-attachments"
        >
          <option value="">Adjuntos: cualquiera</option>
          <option value="true">Con adjuntos</option>
          <option value="false">Sin adjuntos</option>
        </select>
        <Input
          type="date"
          value={filters.state.date_from}
          onChange={(e) => { filters.set("date_from", e.target.value); setPage(1); }}
          className="max-w-[150px]"
          title="Desde"
        />
        <Input
          type="date"
          value={filters.state.date_to}
          onChange={(e) => { filters.set("date_to", e.target.value); setPage(1); }}
          className="max-w-[150px]"
          title="Hasta"
        />
        <SortControl
          value={filters.state.sort_by}
          dir={filters.state.sort_dir}
          options={[
            { v: "received_at", l: "Fecha recibido" },
            { v: "from_email", l: "Remitente" },
            { v: "subject", l: "Asunto" },
          ]}
          onChange={(v, d) => filters.setMany({ sort_by: v, sort_dir: d })}
          testId="inbound-sort"
        />
      </FilterPanel>

      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="text-left p-3 font-medium w-8"></th>
              <th className="text-left p-3 font-medium">Recibido</th>
              <th className="text-left p-3 font-medium">De</th>
              <th className="text-left p-3 font-medium">Para</th>
              <th className="text-left p-3 font-medium">Asunto</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-500">
                <Inbox className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <div>Sin emails recibidos.</div>
                <div className="text-xs text-gray-400 mt-2">Configura el webhook inbound de Resend para recibir respuestas aquí.</div>
              </td></tr>
            )}
            {items.map((it, idx) => {
              const isRead = !!it.read_at;
              const toFull = (it.to_emails || []).join(", ");
              return (
                <tr
                  key={it.id ? `${it.id}-${idx}` : `row-${idx}`}
                  className={`border-t border-gray-100 hover:bg-gray-50/70 cursor-pointer ${
                    !isRead ? "bg-amber-50/40 font-medium" : ""
                  }`}
                  onClick={() => openDetail(it.id)}
                >
                  <td className="p-3">
                    <span className={`inline-block w-2 h-2 rounded-full ${isRead ? "bg-gray-300" : "bg-brand-coral"}`} />
                  </td>
                  <td className="p-3 whitespace-nowrap text-gray-700">{fmtDate(it.received_at)}</td>
                  <td className="p-3 text-gray-800 max-w-[180px] truncate" title={it.from_email}>{it.from_email}</td>
                  <td className="p-3 text-gray-800 max-w-[180px] truncate" title={toFull}>{toFull || <em className="text-gray-400">—</em>}</td>
                  <td className="p-3 text-gray-900 max-w-[360px] truncate" title={it.subject}>{it.subject || <em className="text-gray-400">(sin asunto)</em>}</td>
                  <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => toggleRead(it.id, isRead)} title={isRead ? "Marcar no leído" : "Marcar leído"}>
                      {isRead ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => archive(it.id, it.archived)} title={it.archived ? "Desarchivar" : "Archivar"}>
                      <Archive className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />

      {/* Detalle modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* a11y: DialogTitle siempre presente */}
          <DialogHeader className="sr-only">
            <DialogTitle>Detalle del email recibido</DialogTitle>
            <DialogDescription>{selected?.subject || "Cargando…"}</DialogDescription>
          </DialogHeader>
          {selected && (
            <InboundDetail
              item={selected}
              onClose={() => setSelected(null)}
              onReply={() => openReply(selected)}
            />
          )}
          {loadingDetail && <div className="p-6 text-center text-gray-500">Cargando…</div>}
        </DialogContent>
      </Dialog>

      {/* Reply composer modal */}
      <Dialog open={!!replyComposer} onOpenChange={(open) => !open && setReplyComposer(null)}>
        <DialogContent className="max-w-[1400px] w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="reply-composer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="w-5 h-5 text-brand-burgundy" />
              Responder email
            </DialogTitle>
          </DialogHeader>
          {replyComposer && (
            <div className="mt-2">
              <ComposerForm
                replyTo={replyComposer}
                splitPreview={true}
                onSent={() => {
                  toast.success("Respuesta enviada");
                  setReplyComposer(null);
                  load();
                  onChange?.();
                }}
                onClose={() => setReplyComposer(null)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InboundDetail({ item, onClose, onReply }) {
  const hasHtml = !!(item.html && item.html.trim());
  const hasText = !!(item.text && item.text.trim());
  const [view, setView] = useState(hasHtml ? "html" : "text");

  // Wrap del HTML para asegurar tipografía legible si el remitente envió HTML pelado
  const wrappedHtml = hasHtml
    ? `<!doctype html><html><head><meta charset="utf-8"/><style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.55;color:#1f2937;padding:16px;margin:0;background:#fff;}
        a{color:#6F3947;text-decoration:underline;}
        img{max-width:100%;height:auto;}
        pre,code{background:#f5f5f4;padding:2px 4px;border-radius:2px;font-size:13px;}
        blockquote{border-left:3px solid #d4af37;padding-left:12px;color:#555;margin:8px 0;}
        table{border-collapse:collapse;}
        table td,table th{border:1px solid #e5e7eb;padding:4px 8px;}
      </style></head><body>${item.html}</body></html>`
    : "";

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-base pr-8">{item.subject || "(sin asunto)"}</DialogTitle>
        <DialogDescription asChild>
          <div className="text-xs space-y-1 mt-2 text-muted-foreground">
            <div><strong className="text-gray-700">De:</strong> {item.from_email || "—"}</div>
            {Array.isArray(item.to_emails) && item.to_emails.length > 0 && (
              <div><strong className="text-gray-700">Para:</strong> {item.to_emails.join(", ")}</div>
            )}
            <div><strong className="text-gray-700">Recibido:</strong> {fmtDate(item.received_at)}</div>
            {item.provider_id && (
              <div><strong className="text-gray-700">Provider ID:</strong> <code className="bg-gray-100 px-1">{item.provider_id}</code></div>
            )}
          </div>
        </DialogDescription>
      </DialogHeader>

      {/* Selector de vista (HTML / Texto / Headers) */}
      {(hasHtml || hasText) && (
        <div className="flex gap-1 mt-4 border-b border-gray-200">
          {hasHtml && (
            <button
              onClick={() => setView("html")}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                view === "html" ? "border-brand-burgundy text-brand-burgundy" : "border-transparent text-gray-600 hover:text-brand-burgundy"
              }`}
              data-testid="inbound-view-html"
            >
              HTML
            </button>
          )}
          {hasText && (
            <button
              onClick={() => setView("text")}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                view === "text" ? "border-brand-burgundy text-brand-burgundy" : "border-transparent text-gray-600 hover:text-brand-burgundy"
              }`}
              data-testid="inbound-view-text"
            >
              Texto plano
            </button>
          )}
          <button
            onClick={() => setView("headers")}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
              view === "headers" ? "border-brand-burgundy text-brand-burgundy" : "border-transparent text-gray-600 hover:text-brand-burgundy"
            }`}
            data-testid="inbound-view-headers"
          >
            Headers
          </button>
        </div>
      )}

      <div className="mt-3">
        {!hasHtml && !hasText && (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-sm border border-gray-200">
            <Mail className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <div className="text-sm">Este correo no tiene cuerpo de mensaje.</div>
          </div>
        )}

        {view === "html" && hasHtml && (
          <DevicePreviewIframe srcDoc={wrappedHtml} testIdPrefix="inbound-preview" height={520} />
        )}

        {view === "text" && hasText && (
          <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-sm border border-gray-200 max-h-[500px] overflow-auto">
            {item.text}
          </pre>
        )}

        {view === "headers" && (
          <pre className="whitespace-pre-wrap font-mono text-xs bg-gray-50 p-4 rounded-sm border border-gray-200 max-h-[500px] overflow-auto text-gray-700">
            {item.headers && Object.keys(item.headers).length > 0
              ? JSON.stringify(item.headers, null, 2)
              : "(sin headers)"}
          </pre>
        )}
      </div>

      {Array.isArray(item.attachments) && item.attachments.length > 0 && (
        <div className="mt-4 p-3 border border-gray-200 rounded-sm bg-gray-50">
          <h4 className="text-xs uppercase tracking-wide text-gray-600 font-semibold mb-2">
            Adjuntos ({item.attachments.length})
          </h4>
          <ul className="space-y-1 text-sm">
            {item.attachments.map((att, idx) => (
              <li key={idx} className="text-gray-700">
                • {att.filename || att.name || `Adjunto ${idx + 1}`}
                {att.content_type && <span className="text-xs text-gray-500 ml-2">({att.content_type})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <DialogFooter className="mt-4 flex-wrap gap-2">
        {onReply && (
          <Button onClick={onReply} className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white" data-testid="inbound-reply">
            <Reply className="w-4 h-4 mr-2" /> Responder
          </Button>
        )}
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
      </DialogFooter>
    </>
  );
}

// Helper simple para convertir HTML a texto (usado en quote del reply)
function htmlToTextSimple(html) {
  if (!html) return "";
  if (typeof document === "undefined") return html.replace(/<[^>]+>/g, "");
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

// ─────────────────────────────────────────────────────────────────────────
// Compose tab — usa ComposerForm + lista de borradores
// ─────────────────────────────────────────────────────────────────────────

function ComposeTab({ emailsEnabled, onSent }) {
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [composerKey, setComposerKey] = useState(0); // reset composer
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, subject} | null

  const loadDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const { data } = await api.get("/admin/emails/drafts");
      setDrafts(data.items || []);
    } catch {
      // silent
    } finally {
      setLoadingDrafts(false);
    }
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const openDraft = (id) => {
    setActiveDraftId(id);
    setComposerKey((k) => k + 1);
  };

  const newCompose = () => {
    setActiveDraftId(null);
    setComposerKey((k) => k + 1);
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/admin/emails/drafts/${id}`);
      toast.success("Borrador eliminado");
      if (activeDraftId === id) newCompose();
      setDeleteConfirm(null);
      loadDrafts();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error eliminando");
      setDeleteConfirm(null);
    }
  };

  const requestDelete = (draft) => {
    setDeleteConfirm({ id: draft.id, subject: draft.subject || "(sin asunto)" });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      {/* Sidebar: drafts */}
      <aside className="space-y-2">
        <div className="flex justify-between items-center">
          <h3 className="text-xs uppercase tracking-wider text-gray-600 font-semibold">
            Borradores ({drafts.length})
          </h3>
          <Button size="sm" variant="ghost" onClick={newCompose} title="Nuevo email" data-testid="composer-new">
            <PenSquare className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {loadingDrafts && <div className="text-xs text-gray-400 py-2">Cargando…</div>}
          {!loadingDrafts && drafts.length === 0 && (
            <div className="text-xs text-gray-400 py-3 border border-dashed border-gray-200 rounded-sm p-3 text-center">
              No tienes borradores guardados.
            </div>
          )}
          {drafts.map((d) => (
            <div
              key={d.id}
              className={`group p-2 rounded-sm border cursor-pointer text-sm transition-colors ${
                activeDraftId === d.id
                  ? "border-brand-burgundy bg-brand-cream"
                  : "border-gray-200 hover:border-brand-burgundy/40 hover:bg-gray-50"
              }`}
              onClick={() => openDraft(d.id)}
              data-testid={`draft-${d.id}`}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-gray-900">
                    {d.subject || <em className="text-gray-400">(sin asunto)</em>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {(d.to_emails || []).join(", ") || "(sin destinatario)"}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">{fmtDate(d.updated_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); requestDelete(d); }}
                  className="shrink-0 p-1 rounded-sm text-gray-400 hover:text-red-700 hover:bg-red-50"
                  title="Eliminar borrador"
                  data-testid={`draft-delete-${d.id}`}
                  aria-label="Eliminar borrador"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Composer */}
      <div className="min-w-0">
        <ComposerForm
          key={composerKey}
          draftId={activeDraftId}
          emailsEnabled={emailsEnabled}
          onSent={(logId) => {
            onSent?.(logId);
            setActiveDraftId(null);
            setComposerKey((k) => k + 1);
            loadDrafts();
          }}
        />
      </div>

      {/* Modal confirmar eliminación de borrador */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar borrador</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar el borrador "{deleteConfirm?.subject}"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button
              onClick={() => deleteDraft(deleteConfirm.id)}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="confirm-delete-draft"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Stats tab
// ─────────────────────────────────────────────────────────────────────────

function StatsTab({ stats, onReload }) {
  if (!stats) return <div className="p-6 text-center text-gray-500">Cargando…</div>;
  const ob = stats.outbound || {};
  const ib = stats.inbound || {};
  const ev = stats.events || {};
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button variant="outline" onClick={onReload} size="sm"><RefreshCw className="w-4 h-4 mr-2" /> Refrescar</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card title="Salientes (logs)">
          <Metric label="Total" value={ob.total} />
          <Metric label="Entregados" value={ob.sent} color="text-emerald-700" />
          <Metric label="Bloqueados (kill switch)" value={ob.killed} color="text-gray-600" />
          <Metric label="Secuencia inactiva" value={ob.sequence_disabled} color="text-amber-700" />
          <Metric label="Bounced" value={ob.bounced} color="text-red-700" />
          <Metric label="Fallidos" value={ob.failed} color="text-red-700" />
          <Metric label="Suprimidos" value={ob.suppressed} color="text-gray-700" />
          <Metric label="Errores" value={ob.error} color="text-red-700" />
        </Card>
        <Card title="Entrantes">
          <Metric label="Total" value={ib.total} />
          <Metric label="No leídos" value={ib.unread} color="text-brand-coral" />
        </Card>
        <Card title="Eventos Resend">
          <Metric label="Total dominio" value={ev.total_domain} color="text-brand-burgundy" />
          <div className="text-[11px] text-gray-500 mt-2">
            Eventos webhook registrados para <code className="bg-gray-100 px-1">procure.justsapiens.com</code>.
          </div>
        </Card>
      </div>
      <div className="text-xs text-gray-500">
        Estado del kill switch: <strong>{stats.emails_enabled ? "ACTIVO" : "DESACTIVADO"}</strong>
        {" · "}Templates disponibles: {(stats.templates || []).join(", ")}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-gray-200 rounded-sm bg-white p-5">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-sm text-gray-700">{label}</span>
      <span className={`font-serif text-2xl ${color || "text-gray-900"}`}>{value ?? 0}</span>
    </div>
  );
}

function Pagination({ page, pageSize, total, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
      <span>Página {page} de {totalPages} · {total} resultados</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onChange(page - 1)}>Anterior</Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Siguiente</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Events tab — Feed cronológico de los 11 tipos de evento Resend
// ─────────────────────────────────────────────────────────────────────────

const EVENT_META = {
  "email.sent":             { label: "Enviado",       Icon: Send,                color: "bg-blue-100 text-blue-800 border-blue-200" },
  "email.scheduled":        { label: "Programado",    Icon: Calendar,            color: "bg-slate-100 text-slate-800 border-slate-200" },
  "email.delivered":        { label: "Entregado",     Icon: CheckCheck,          color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "email.delivery_delayed": { label: "Demorado",      Icon: Clock,               color: "bg-amber-100 text-amber-800 border-amber-200" },
  "email.opened":           { label: "Abierto",       Icon: MailOpen,            color: "bg-violet-100 text-violet-800 border-violet-200" },
  "email.clicked":          { label: "Click",         Icon: MousePointerClick,   color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  "email.bounced":          { label: "Bounced",       Icon: AlertTriangle,       color: "bg-red-100 text-red-800 border-red-200" },
  "email.complained":       { label: "Spam (queja)",  Icon: ShieldAlert,         color: "bg-orange-100 text-orange-800 border-orange-200" },
  "email.failed":           { label: "Fallido",       Icon: AlertTriangle,       color: "bg-red-100 text-red-700 border-red-300" },
  "email.suppressed":       { label: "Suprimido",     Icon: Ban,                 color: "bg-gray-200 text-gray-800 border-gray-300" },
  "email.received":         { label: "Recibido",      Icon: Inbox,               color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};

function EventBadge({ eventType }) {
  const meta = EVENT_META[eventType] || { label: eventType, Icon: Activity, color: "bg-gray-100 text-gray-700 border-gray-200" };
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border rounded-sm ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function EventsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const filters = useEmailFilters({
    q: "",
    event_type: "",
    email_search: "",
    email_field: "any",
    provider_id: "",
    date_from: "",
    date_to: "",
    sort_by: "occurred_at",
    sort_dir: "desc",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filters.toQueryParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const [eventsRes, statsRes] = await Promise.all([
        api.get(`/admin/emails/events?${params}`),
        api.get(`/admin/emails/events/stats?days=30&only_domain=false`),
      ]);
      setItems(eventsRes.data.items || []);
      setTotal(eventsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando eventos");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  // Polling cada 10s para eventos en tiempo real desde Resend
  usePolling(load, 10000, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/emails/events/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error abriendo evento");
    }
  };

  const counts = stats?.counts || {};
  const eventTypeOptions = Object.keys(EVENT_META);

  return (
    <div>
      <div className="mb-4 p-3 bg-brand-cream border border-brand-burgundy/20 rounded-sm text-sm text-gray-700">
        <div className="flex items-center gap-2 mb-1 text-brand-burgundy font-medium">
          <Activity className="w-4 h-4" /> Feed de eventos
        </div>
        Registro cronológico de los 11 eventos Resend habilitados (sent, scheduled, delivered, delivery_delayed,
        opened, clicked, bounced, complained, failed, suppressed, received). Por defecto solo muestra eventos
        del dominio <code className="bg-white px-1 py-0.5 rounded-sm text-brand-burgundy">procure.justsapiens.com</code>.
      </div>

      {/* Mini stats */}
      {stats && Object.keys(counts).length > 0 && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-2">
          {Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([type, n]) => (
            <div key={type} className="border border-gray-200 rounded-sm p-2 bg-white">
              <div className="flex items-center justify-between gap-2">
                <EventBadge eventType={type} />
                <span className="font-serif text-lg text-gray-900">{n}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <FilterPanel activeCount={filters.activeCount} onReset={() => { filters.reset(); setPage(1); }} onReload={load} loading={loading} testIdPrefix="events">
        <Input
          placeholder="Búsqueda libre (remitente, asunto, provider_id)"
          value={filters.state.q}
          onChange={(e) => filters.set("q", e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
          data-testid="events-search"
        />
        <select
          value={filters.state.event_type}
          onChange={(e) => { filters.set("event_type", e.target.value); setPage(1); }}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="events-type-filter"
        >
          <option value="">Todos los tipos</option>
          {eventTypeOptions.map((t) => (
            <option key={t} value={t}>{EVENT_META[t].label}</option>
          ))}
        </select>
        <Input
          placeholder="Provider ID exacto"
          value={filters.state.provider_id}
          onChange={(e) => filters.set("provider_id", e.target.value)}
          data-testid="events-provider-id"
          className="max-w-[220px]"
        />
        <EmailSearchInput
          email={filters.state.email_search}
          field={filters.state.email_field}
          onChange={(email, field) => { filters.setMany({ email_search: email, email_field: field }); setPage(1); }}
          testIdPrefix="events"
        />
        <Input
          type="date"
          value={filters.state.date_from}
          onChange={(e) => { filters.set("date_from", e.target.value); setPage(1); }}
          className="max-w-[150px]"
          title="Desde"
        />
        <Input
          type="date"
          value={filters.state.date_to}
          onChange={(e) => { filters.set("date_to", e.target.value); setPage(1); }}
          className="max-w-[150px]"
          title="Hasta"
        />
        <SortControl
          value={filters.state.sort_by}
          dir={filters.state.sort_dir}
          options={[
            { v: "occurred_at", l: "Fecha" },
            { v: "event_type", l: "Tipo evento" },
            { v: "from_email", l: "Remitente" },
          ]}
          onChange={(v, d) => filters.setMany({ sort_by: v, sort_dir: d })}
          testId="events-sort"
        />
      </FilterPanel>

      {/* Lista cronológica */}
      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
            <tr>
              <th className="text-left p-3 font-medium">Fecha/hora</th>
              <th className="text-left p-3 font-medium">Evento</th>
              <th className="text-left p-3 font-medium">De</th>
              <th className="text-left p-3 font-medium">Para</th>
              <th className="text-left p-3 font-medium">Asunto</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <div>Sin eventos registrados aún.</div>
                <div className="text-xs text-gray-400 mt-2">Los eventos aparecen cuando Resend envía webhooks (entregas, aperturas, clicks, bounces, etc.).</div>
              </td></tr>
            )}
            {items.map((evt) => (
              <tr
                key={evt.id}
                className="border-t border-gray-100 hover:bg-gray-50/50 cursor-pointer"
                onClick={() => openDetail(evt.id)}
                data-testid={`event-row-${evt.id}`}
              >
                <td className="p-3 whitespace-nowrap text-gray-700 text-xs">{fmtDate(evt.occurred_at)}</td>
                <td className="p-3"><EventBadge eventType={evt.event_type} /></td>
                <td className="p-3 text-xs text-gray-700 max-w-[180px] truncate" title={evt.from_email}>{evt.from_email || <em className="text-gray-400">—</em>}</td>
                <td className="p-3 text-xs text-gray-700 max-w-[180px] truncate" title={(evt.to_emails || []).join(", ")}>{(evt.to_emails || []).join(", ") || <em className="text-gray-400">—</em>}</td>
                <td className="p-3 text-gray-800 max-w-[260px] truncate" title={evt.subject}>{evt.subject || <em className="text-gray-400">(sin asunto)</em>}</td>
                <td className="p-3 text-right">
                  {evt.domain_match && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium" title="Dominio procure.justsapiens.com">dominio</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />

      {/* Detalle modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <EventBadge eventType={selected.event_type} />
                  <span className="text-sm font-normal text-gray-600">{fmtDate(selected.occurred_at)}</span>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Provider ID: <code className="bg-gray-100 px-1.5 py-0.5">{selected.provider_id || "—"}</code>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-4 text-sm">
                <div><strong>De:</strong> {selected.from_email || "—"}</div>
                <div><strong>Para:</strong> {(selected.to_emails || []).join(", ") || "—"}</div>
                <div><strong>Asunto:</strong> {selected.subject || <em className="text-gray-400">(sin asunto)</em>}</div>
                <div><strong>Estado:</strong> {selected.status}</div>
                <div><strong>Dominio coincide:</strong> {selected.domain_match ? "✅ Sí" : "❌ No"}</div>
                {selected.email_log_id && (
                  <div><strong>Email log:</strong> <code className="text-xs bg-gray-100 px-1">{selected.email_log_id}</code></div>
                )}
                {selected.inbound_id && (
                  <div><strong>Inbound ID:</strong> <code className="text-xs bg-gray-100 px-1">{selected.inbound_id}</code></div>
                )}
                {selected.raw_data && Object.keys(selected.raw_data).length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-gray-700 hover:text-brand-burgundy">Ver payload completo</summary>
                    <pre className="mt-2 bg-gray-50 p-3 text-[11px] overflow-auto max-h-80 rounded-sm border border-gray-200">
                      {JSON.stringify(selected.raw_data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setSelected(null)}>Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sequences tab — Plan A1: toggle por trigger
// ─────────────────────────────────────────────────────────────────────────

function SequencesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const filters = useEmailFilters({
    q: "",
    active_filter: "",
    sort_by: "trigger_event",
    sort_dir: "asc",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filters.toQueryParams();
      const qs = params.toString();
      const { data } = await api.get(`/admin/emails/sequences${qs ? `?${qs}` : ""}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando secuencias");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (seq, next) => {
    if (seq.is_critical && !next) {
      toast.warning("Esta secuencia es crítica y no puede desactivarse.");
      return;
    }
    setSavingId(seq.id);
    try {
      await api.patch(`/admin/emails/sequences/${seq.id}`, { active: !!next });
      toast.success(next ? `Secuencia activada: ${seq.name}` : `Secuencia desactivada: ${seq.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error actualizando");
    } finally {
      setSavingId(null);
    }
  };

  const saveDescription = async (seq, newDesc) => {
    setSavingId(seq.id);
    try {
      await api.patch(`/admin/emails/sequences/${seq.id}`, { description: newDesc });
      toast.success("Descripción actualizada");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSavingId(null);
    }
  };

  // Iter23 — Editar campos avanzados DB-driven
  const saveAdvanced = async (seq, patch) => {
    setSavingId(seq.id);
    try {
      await api.patch(`/admin/emails/sequences/${seq.id}`, patch);
      toast.success("Configuración actualizada");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <div className="mb-4 p-4 bg-brand-cream border border-brand-burgundy/20 rounded-sm">
        <h3 className="font-medium text-brand-burgundy mb-1 flex items-center gap-2">
          <Workflow className="w-4 h-4" /> Sobre las secuencias automatizadas
        </h3>
        <p className="text-sm text-gray-700">
          Cada secuencia representa un disparador automático del sistema. Cuando una secuencia está
          <strong> desactivada</strong>, ningún email de ese tipo se envía (excepto las críticas, que
          siempre se envían por seguridad). El composer manual (admin) sigue funcionando sin restricción.
        </p>
      </div>

      <FilterPanel activeCount={filters.activeCount} onReset={filters.reset} onReload={load} loading={loading} testIdPrefix="sequences">
        <Input
          placeholder="Búsqueda libre (nombre, trigger, template)"
          value={filters.state.q}
          onChange={(e) => filters.set("q", e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          data-testid="sequences-search"
        />
        <select
          value={filters.state.active_filter}
          onChange={(e) => filters.set("active_filter", e.target.value)}
          className="h-10 border border-gray-300 rounded-sm px-3 text-sm"
          data-testid="sequences-active-filter"
        >
          <option value="">Todas</option>
          <option value="active">Solo activas</option>
          <option value="inactive">Solo inactivas</option>
          <option value="critical">Solo críticas</option>
        </select>
        <SortControl
          value={filters.state.sort_by}
          dir={filters.state.sort_dir}
          options={[
            { v: "trigger_event", l: "Trigger" },
            { v: "name", l: "Nombre" },
            { v: "updated_at", l: "Última actualización" },
            { v: "send_count", l: "Envíos 30d" },
          ]}
          onChange={(v, d) => filters.setMany({ sort_by: v, sort_dir: d })}
          testId="sequences-sort"
        />
      </FilterPanel>

      <div className="space-y-3">
        {loading && items.length === 0 && (
          <div className="p-8 text-center text-gray-500">Cargando…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-gray-500 border border-dashed border-gray-200 rounded-sm">
            Sin secuencias que coincidan con los filtros.
          </div>
        )}
        {items.map((seq) => (
          <SequenceCard
            key={seq.id}
            seq={seq}
            saving={savingId === seq.id}
            onToggle={(next) => toggle(seq, next)}
            onSaveDescription={(desc) => saveDescription(seq, desc)}
            onSaveAdvanced={(patch) => saveAdvanced(seq, patch)}
          />
        ))}
      </div>
    </div>
  );
}

function SequenceCard({ seq, saving, onToggle, onSaveDescription, onSaveAdvanced }) {
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(seq.description || "");
  const stats = seq.stats || {};
  const total30d = (stats.sent || 0) + (stats.delivered || 0) + (stats.bounced || 0) + (stats.error || 0);

  React.useEffect(() => { setDesc(seq.description || ""); }, [seq.description]);

  const handleSaveDesc = () => {
    if (desc.trim() !== (seq.description || "").trim()) {
      onSaveDescription(desc.trim());
    }
    setEditingDesc(false);
  };

  return (
    <div
      className={`border rounded-sm p-4 transition-colors ${
        seq.active ? "border-emerald-200 bg-white" : "border-amber-300 bg-amber-50/40"
      }`}
      data-testid={`sequence-card-${seq.trigger_event}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="font-medium text-gray-900">{seq.name}</h4>
            {seq.is_critical && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-sm bg-blue-100 text-blue-800 border border-blue-300">
                <Lock className="w-3 h-3" /> Crítica
              </span>
            )}
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${
              seq.active ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
            }`}>
              {seq.active ? "ACTIVA" : "INACTIVA"}
            </span>
          </div>
          <div className="text-xs text-gray-500 mb-2 flex gap-3 flex-wrap">
            <span>Trigger: <code className="bg-gray-100 px-1 py-0.5 rounded-sm text-gray-700">{seq.trigger_event}</code></span>
            <span>Template: <code className="bg-gray-100 px-1 py-0.5 rounded-sm text-gray-700">{seq.template_name}</code></span>
          </div>
          {!editingDesc ? (
            <p
              className="text-sm text-gray-700 cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded-sm"
              onClick={() => setEditingDesc(true)}
              title="Click para editar"
            >
              {seq.description || <em className="text-gray-400">Sin descripción (click para añadir)</em>}
            </p>
          ) : (
            <div className="flex gap-2 items-start">
              <Textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                rows={2}
                className="text-sm"
                autoFocus
              />
              <div className="flex flex-col gap-1">
                <Button size="sm" onClick={handleSaveDesc} className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white">Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => { setDesc(seq.description || ""); setEditingDesc(false); }}>Cancelar</Button>
              </div>
            </div>
          )}
          {total30d > 0 && (
            <div className="mt-3 text-xs text-gray-500 flex gap-3 flex-wrap">
              <span>📊 Últimos 30 días:</span>
              {(stats.sent || 0) > 0 && <span className="text-blue-700">{stats.sent} enviados</span>}
              {(stats.delivered || 0) > 0 && <span className="text-emerald-700">{stats.delivered} entregados</span>}
              {(stats.bounced || 0) > 0 && <span className="text-red-700">{stats.bounced} bounced</span>}
              {(stats.sequence_disabled || 0) > 0 && <span className="text-amber-700">{stats.sequence_disabled} bloqueados</span>}
              {(stats.killed || 0) > 0 && <span className="text-gray-600">{stats.killed} kill switch</span>}
            </div>
          )}

          {/* Iter23 — Editor avanzado de campos DB-driven */}
          {(seq.trigger_type === "transition" || seq.trigger_type === "cron") && (
            <SequenceAdvancedEditor seq={seq} onSave={onSaveAdvanced} saving={saving} />
          )}
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
          <Switch
            checked={!!seq.active}
            onCheckedChange={onToggle}
            disabled={saving || seq.is_critical}
            data-testid={`sequence-toggle-${seq.trigger_event}`}
          />
          <Link
            to={`/admin/emails/sequences/${seq.id}/runs`}
            className="text-[10px] uppercase tracking-widest text-brand-burgundy hover:underline"
            data-testid={`sequence-runs-link-${seq.trigger_event}`}
          >
            Aplicaciones →
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared filter components (used across all 4 tabs)
// ─────────────────────────────────────────────────────────────────────────

function FilterPanel({ children, activeCount, onReset, onReload, loading, testIdPrefix }) {
  return (
    <div className="mb-4 p-3 border border-gray-200 rounded-sm bg-gray-50/50">
      <div className="flex items-center gap-2 mb-2">
        <Filter className="w-4 h-4 text-gray-500" />
        <span className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Filtros</span>
        {activeCount > 0 && (
          <span className="text-[10px] bg-brand-burgundy text-white px-1.5 py-0.5 rounded-sm font-semibold">
            {activeCount} activo{activeCount === 1 ? "" : "s"}
          </span>
        )}
        <div className="flex-1" />
        {activeCount > 0 && (
          <Button size="sm" variant="ghost" onClick={onReset} className="h-8 text-xs" data-testid={`${testIdPrefix}-reset`}>
            <X className="w-3 h-3 mr-1" /> Limpiar
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onReload} disabled={loading} className="h-8" data-testid={`${testIdPrefix}-reload`}>
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {children}
      </div>
    </div>
  );
}

function SortControl({ value, dir, options, onChange, testId }) {
  return (
    <div className="inline-flex items-center gap-1 border border-gray-300 rounded-sm bg-white h-10 px-2" data-testid={testId}>
      <ArrowUpDown className="w-3 h-3 text-gray-400" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value, dir)}
        className="text-sm border-0 outline-none bg-transparent"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      <button
        type="button"
        onClick={() => onChange(value, dir === "desc" ? "asc" : "desc")}
        className="text-xs px-1.5 py-0.5 rounded-sm hover:bg-gray-100 text-gray-600"
        title={dir === "desc" ? "Descendente" : "Ascendente"}
      >
        {dir === "desc" ? "↓" : "↑"}
      </button>
    </div>
  );
}

/**
 * EmailSearchInput — input editable de email con selector de campo (from/to/any).
 * Pre-llena con sugerencia "@procure.justsapiens.com" al hacer focus en input vacío.
 */
function EmailSearchInput({ email, field, onChange, testIdPrefix }) {
  const handleFocus = (e) => {
    if (!email) {
      // Sugerencia inicial
      e.target.placeholder = "ej: @procure.justsapiens.com";
    }
  };

  return (
    <div className="inline-flex items-stretch gap-0 border border-gray-300 rounded-sm bg-white h-10" data-testid={`${testIdPrefix}-email-search-group`}>
      <select
        value={field}
        onChange={(e) => onChange(email, e.target.value)}
        className="text-xs border-0 outline-none bg-gray-50 px-2 border-r border-gray-300 rounded-l-sm"
        data-testid={`${testIdPrefix}-email-field`}
        title="Buscar en"
      >
        <option value="any">From o To</option>
        <option value="from">Solo From</option>
        <option value="to">Solo To</option>
      </select>
      <input
        type="text"
        value={email}
        onChange={(e) => onChange(e.target.value, field)}
        onFocus={handleFocus}
        placeholder="filtrar por email…"
        className="text-sm outline-none bg-transparent px-2 w-[200px]"
        data-testid={`${testIdPrefix}-email-search`}
      />
      {email && (
        <button
          type="button"
          onClick={() => onChange("", field)}
          className="text-gray-400 hover:text-red-700 px-2"
          title="Limpiar"
          data-testid={`${testIdPrefix}-email-clear`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// Iter23 — Editor inline de campos avanzados DB-driven (delay/cron/roles)
function SequenceAdvancedEditor({ seq, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [delay, setDelay] = useState(String(seq.delay_minutes ?? 0));
  const [cronDays, setCronDays] = useState((seq.cron_days || []).join(", "));
  const [rolesExclude, setRolesExclude] = useState((seq.filter_roles_exclude || []).join(", "));
  // Iter24 — Preview live state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewCtxJson, setPreviewCtxJson] = useState("");

  const loadPreview = async (overrides = null) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const body = overrides ? { sample_values: overrides } : {};
      const r = await api.post(`/api/admin/emails/sequences/${seq.id}/preview`, body);
      setPreviewData(r);
      if (!previewCtxJson && r?.context_used) {
        setPreviewCtxJson(JSON.stringify(r.context_used, null, 2));
      }
    } catch (e) {
      setPreviewError(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = () => {
    setPreviewOpen(true);
    setPreviewData(null);
    setPreviewCtxJson("");
    loadPreview();
  };

  const reRenderWithJson = () => {
    let parsed = null;
    try {
      parsed = previewCtxJson.trim() ? JSON.parse(previewCtxJson) : null;
    } catch {
      setPreviewError("JSON de contexto inválido");
      return;
    }
    loadPreview(parsed);
  };

  const submit = (e) => {
    e?.preventDefault?.();
    const patch = {};
    const dm = parseInt(delay, 10);
    if (!isNaN(dm) && dm !== (seq.delay_minutes ?? 0)) patch.delay_minutes = dm;
    if (seq.trigger_type === "cron") {
      const days = cronDays.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
      patch.cron_days = days;
    }
    const roles = rolesExclude.split(",").map((s) => s.trim()).filter(Boolean);
    patch.filter_roles_exclude = roles;
    if (Object.keys(patch).length === 0) {
      setOpen(false);
      return;
    }
    onSave(patch);
    setOpen(false);
  };

  const summaryParts = [];
  if (seq.trigger_type === "transition") {
    summaryParts.push(`→ ${seq.target_state}`);
    summaryParts.push(`delay ${seq.delay_minutes >= 1440 ? `${Math.round(seq.delay_minutes/1440)}d` : `${seq.delay_minutes}m`}`);
  } else if (seq.trigger_type === "cron") {
    summaryParts.push(`días [${(seq.cron_days || []).join(", ")}]`);
  }
  if ((seq.filter_roles_exclude || []).length > 0) {
    summaryParts.push(`excluye ${seq.filter_roles_exclude.join(",")}`);
  }

  if (!open) {
    return (
      <>
        <div className="mt-3 pt-3 border-t border-dashed border-gray-200 text-xs text-gray-500 flex flex-wrap items-center gap-2" data-testid={`seq-advanced-summary-${seq.trigger_event}`}>
          <span className="uppercase tracking-widest text-[10px]">Config</span>
          {summaryParts.map((s, i) => (
            <code key={i} className="bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-700">{s}</code>
          ))}
          <button
            type="button"
            onClick={openPreview}
            className="text-brand-burgundy hover:underline ml-auto"
            data-testid={`seq-preview-${seq.trigger_event}`}
          >
            Preview →
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-brand-burgundy hover:underline"
            data-testid={`seq-advanced-edit-${seq.trigger_event}`}
          >
            Editar →
          </button>
        </div>
        <SequencePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          seq={seq}
          loading={previewLoading}
          data={previewData}
          error={previewError}
          ctxJson={previewCtxJson}
          setCtxJson={setPreviewCtxJson}
          onReRender={reRenderWithJson}
        />
      </>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-2" data-testid={`seq-advanced-form-${seq.trigger_event}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="block text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">Delay (minutos)</span>
          <input
            type="number"
            min={0}
            max={43200}
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
            className="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm"
            data-testid={`seq-advanced-delay-${seq.trigger_event}`}
          />
        </label>
        {seq.trigger_type === "cron" && (
          <label className="text-xs">
            <span className="block text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">Días cron (-1 = último)</span>
            <input
              type="text"
              value={cronDays}
              onChange={(e) => setCronDays(e.target.value)}
              placeholder="25, 28, -1"
              className="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm font-mono"
              data-testid={`seq-advanced-crondays-${seq.trigger_event}`}
            />
          </label>
        )}
        <label className="text-xs sm:col-span-2">
          <span className="block text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">Excluir roles (CSV)</span>
          <input
            type="text"
            value={rolesExclude}
            onChange={(e) => setRolesExclude(e.target.value)}
            placeholder="admin, reviewer"
            className="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm font-mono"
            data-testid={`seq-advanced-roles-${seq.trigger_event}`}
          />
        </label>
      </div>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Button>
        <Button size="sm" type="submit" disabled={saving} className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white" data-testid={`seq-advanced-save-${seq.trigger_event}`}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </form>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// Iter24 — SequencePreviewDialog: preview live con motor Jinja2 puro.
// Muestra subject + html renderizado con contexto demo editable (JSON).
// ─────────────────────────────────────────────────────────────────────────
function SequencePreviewDialog({
  open, onOpenChange, seq, loading, data, error, ctxJson, setCtxJson, onReRender,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`seq-preview-dialog-${seq.trigger_event}`}>
        <DialogHeader>
          <DialogTitle>Preview · {seq.name || seq.trigger_event}</DialogTitle>
          <DialogDescription>
            Renderiza el template ligado con motor Jinja2 puro. Edita el contexto JSON para
            probar condicionales <code>{`{% if %}`}</code> y loops <code>{`{% for %}`}</code>.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-gray-500 py-8 text-center" data-testid="seq-preview-loading">
            Cargando preview…
          </div>
        )}

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="seq-preview-error">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-3">
            <div className="text-xs">
              <span className="block uppercase tracking-widest text-gray-500 mb-1">Asunto renderizado</span>
              <div className="bg-gray-50 border border-gray-200 px-2 py-1.5 rounded-sm font-medium text-gray-800" data-testid="seq-preview-subject">
                {data.subject || "(sin asunto)"}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div>
                <span className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Cuerpo HTML</span>
                <DevicePreviewIframe
                  srcDoc={data.html}
                  testIdPrefix="seq-preview"
                  height={460}
                />
              </div>
              <div className="text-xs">
                <span className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                  Contexto (JSON editable)
                </span>
                <textarea
                  value={ctxJson}
                  onChange={(e) => setCtxJson(e.target.value)}
                  className="w-full h-[420px] border border-gray-300 rounded-sm px-2 py-1.5 font-mono text-[11px] leading-snug"
                  data-testid="seq-preview-ctx-json"
                  spellCheck={false}
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-gray-500">
                    {(data.variables_declared || []).length > 0 && (
                      <>Variables declaradas: {(data.variables_declared || []).join(", ")}</>
                    )}
                  </span>
                  <Button
                    size="sm"
                    onClick={onReRender}
                    className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
                    data-testid="seq-preview-rerender"
                  >
                    Re-renderizar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="seq-preview-close">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
